import React, { useEffect, useState } from "react";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import DbService from "../services/DbService";
import { formatISODate } from "../utils/dateUtils";
import { TeamMember } from "../types/TeamModel";
import { TeamMemberEntryForm } from "./forms/TeamMemberForm";
import { DynamicTableListTemplate } from "./shared/DynamicTableListTemplate";
import { enqueueSnackbar } from "notistack";
import ActionsCell from "./actionsCell";
import { useHandleError } from "../hooks/useHandleError";

const TeamMemberList: React.FC = () => {
  const handleError = useHandleError();

  const [loading, setLoading] = useState<boolean>(true);

  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [teamMemberToDelete, setTeamMemberToDelete] =
    useState<TeamMember | null>(null);

  const [selectedTeamMember, setSelectedTeamMember] = useState<
    TeamMember | undefined
  >(undefined);

  const hasFetchedRef = React.useRef(false);

  const columns: GridColDef[] = [
    { field: "email", headerName: "Email", width: 200 },
    { field: "role", headerName: "Role", width: 150 },
    { field: "status", headerName: "Status", width: 200 },
    {
      field: "invitationSentAt",
      headerName: "Invitations Sent At",
      width: 250,
      renderCell: (params) =>
        params.value
          ?.map((date: string) => formatISODate(date).datePart)
          .join(", ") || "",
    },
    {
      field: "registeredAt",
      headerName: "Registered At",
      width: 200,
      renderCell: (params) =>
        formatISODate(params.value as string).datePart || "",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        const row: TeamMember = params.row as TeamMember;
        return (
          <ActionsCell
            onEdit={() => handleEdit(row)}
            onDelete={() => handleDelete(row)}
          />
        );
      },
    },
  ];

  useEffect(() => {
    const fetchTeamMembers = async () => {
      setLoading(true);
      try {
        const response = await DbService.getTeamMembers();
        setTeamMembers(response.data);
      } catch (error) {
        handleError(error);
      } finally {
        setLoading(false);
      }
    };
    if (!hasFetchedRef.current) {
      fetchTeamMembers();
      hasFetchedRef.current = true;
    }
  }, []);

  const handleSubmit = async (data: TeamMember) => {
    if (data?._id) {
      handleUpdate(data);
    } else {
      try {
        const response = await DbService.createTeamMember(data);
        setTeamMembers([response.data, ...teamMembers]);
        setSelectedTeamMember(response.data);
        enqueueSnackbar(response.message, {
          variant: "success",
        });
      } catch (error) {
        handleError(error);
      }
    }
  };

  const handleUpdate = async (data: TeamMember) => {
    if (!data._id) {
      enqueueSnackbar(`id not found, update failed.`, {
        variant: "error",
      });

      return;
    }

    try {
      const response = await DbService.updateTeamMember(data._id, data);
      setTeamMembers((prevTeamMembers) =>
        prevTeamMembers.map((tm) => (tm._id === data._id ? response.data : tm))
      );

      setSelectedTeamMember(data);

      enqueueSnackbar(response.message, {
        variant: "success",
      });
    } catch (error) {
      handleError(error);
    }
  };

  const handleEdit = (row: TeamMember) => {
    setSelectedTeamMember(row);
  };

  const handleCloseDialog = () => {
    setSelectedTeamMember(undefined);
  };

  const handleDelete = async (row: TeamMember) => {
    if (!row?._id) {
      enqueueSnackbar(`Id is missing from this ${row.email}`, {
        variant: "error",
      });
      return;
    }
    setTeamMemberToDelete(row);
  };

  const handleDeleteConfirm = async (row: TeamMember | null | undefined) => {
    if (row?._id) {
      try {
        const response = await DbService.deleteTeamMember(row._id);
        // Update the state by filtering out the deleted school
        setTeamMembers((prevTeamMembers) =>
          prevTeamMembers.filter((t) => t._id !== row._id)
        );
        enqueueSnackbar(response.message, {
          variant: "success",
        });
      } catch (error) {
        handleError(error);
      }
    }
    setTeamMemberToDelete(null);
  };

  return (
    <DynamicTableListTemplate<TeamMember>
      columns={columns}
      data={teamMembers}
      loading={loading}
      selectedRow={selectedTeamMember}
      FormComponent={
        <TeamMemberEntryForm
          selectedRow={selectedTeamMember}
          onSubmit={handleSubmit}
        />
      }
      entityLabel="Team Member"
      dialogTitle="Team Member Entry Form"
      onClearSelectedRow={handleCloseDialog}
      onConfirmDelete={handleDeleteConfirm}
      entityToDelete={teamMemberToDelete}
    />
  );
};

export default TeamMemberList;
